import { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../services/api'

interface MailboxUsageData {
  userPrincipalName: string
  displayName: string
  isDeleted: boolean
  deletedDate: string | null
  createdDate: string | null
  lastActivityDate: string | null
  itemCount: number
  storageUsedInBytes: number
  issueWarningQuotaInBytes: number
  prohibitSendQuotaInBytes: number
  prohibitSendReceiveQuotaInBytes: number
  hasArchive: boolean
  reportRefreshDate: string
}

interface MailboxUsageResponse {
  usage: MailboxUsageData[]
  isObfuscated: boolean
  totalRecords: number
  reportDate: string
}

interface MailSectionProps {
  userId: string
  userPrincipalName: string
  displayName: string
  customerId: string
  customerTenantId: string
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never'
  try {
    return new Date(dateStr).toLocaleDateString()
  } catch {
    return dateStr
  }
}

export function MailSection({
  userId: _userId,
  userPrincipalName,
  displayName,
  customerId,
  customerTenantId,
}: MailSectionProps) {
  const { getCustomerGraphAccessToken } = useAuth()
  const [usage, setUsage] = useState<MailboxUsageData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isObfuscated, setIsObfuscated] = useState(false)

  useEffect(() => {
    const fetchMailboxUsage = async () => {
      try {
        setLoading(true)
        setError(null)
        setIsObfuscated(false)
        const token = await getCustomerGraphAccessToken(customerTenantId)
        // Pass both UPN and displayName for matching (display name is fallback when UPNs are obfuscated)
        const response = await api.getM365MailboxUsage(token, customerId, userPrincipalName, displayName) as MailboxUsageResponse

        // Check if data is obfuscated
        if (response.isObfuscated) {
          setIsObfuscated(true)
          setUsage(null)
        } else if (response.usage && response.usage.length > 0) {
          setUsage(response.usage[0])
        } else {
          setUsage(null)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load mailbox info')
      } finally {
        setLoading(false)
      }
    }
    fetchMailboxUsage()
  }, [customerId, customerTenantId, userPrincipalName, displayName, getCustomerGraphAccessToken])

  // Build Microsoft Admin Center URL for this user
  const getAdminCenterUserUrl = () => {
    return `https://admin.microsoft.com/#/users/:/UserDetails/${encodeURIComponent(userPrincipalName)}/Account`
  }

  const getExchangeAdminCenterUrl = () => {
    return `https://admin.exchange.microsoft.com/#/mailboxes/${encodeURIComponent(userPrincipalName)}`
  }

  // Exchange Admin Center URLs for specific actions
  const getOutOfOfficeUrl = () => {
    return `https://admin.exchange.microsoft.com/#/mailboxes/${encodeURIComponent(userPrincipalName)}/mailflow`
  }

  const getMailboxDelegationUrl = () => {
    return `https://admin.exchange.microsoft.com/#/mailboxes/${encodeURIComponent(userPrincipalName)}/mailboxpermissions`
  }

  const getConvertMailboxUrl = () => {
    return `https://admin.exchange.microsoft.com/#/mailboxes/${encodeURIComponent(userPrincipalName)}/general`
  }

  // Calculate storage percentage
  const getStoragePercentage = (): number => {
    if (!usage || !usage.prohibitSendReceiveQuotaInBytes) return 0
    return Math.min(100, (usage.storageUsedInBytes / usage.prohibitSendReceiveQuotaInBytes) * 100)
  }

  const getStorageStatus = (): { color: string; label: string } => {
    const percentage = getStoragePercentage()
    if (percentage >= 90) return { color: 'red', label: 'Critical' }
    if (percentage >= 75) return { color: 'yellow', label: 'Warning' }
    return { color: 'green', label: 'Normal' }
  }

  if (loading) {
    return (
      <div className="bg-white shadow sm:rounded-lg p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            <p className="mt-2 text-sm text-gray-500">Loading mailbox info...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white shadow sm:rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Mail</h3>
          <a
            href={getExchangeAdminCenterUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            Open in Exchange Admin
          </a>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="text-sm text-yellow-700">
              <p className="font-medium">Mailbox usage data unavailable</p>
              <p className="mt-1">This may be because the Reports.Read.All permission needs to be granted, or there's no mailbox data available for this user yet.</p>
              <p className="mt-2">
                Use the{' '}
                <a
                  href={getExchangeAdminCenterUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline hover:text-yellow-800"
                >
                  Exchange Admin Center
                </a>
                {' '}to view and manage mailbox settings.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show specific message when data is obfuscated due to privacy settings
  if (isObfuscated) {
    // Direct link to M365 Admin Center Reports settings
    const getReportsSettingsUrl = () => {
      return 'https://admin.microsoft.com/Adminportal/Home#/Settings/Services/:/Settings/L1/Reports'
    }

    return (
      <div className="bg-white shadow sm:rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Mail</h3>
          <a
            href={getExchangeAdminCenterUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            Open in Exchange Admin
          </a>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-amber-400 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v.01M12 12v-2m0-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm text-amber-800">
              <p className="font-medium mb-2">Mailbox data is concealed</p>
              <p className="mb-2">
                The customer tenant has a privacy setting enabled that conceals user names in usage reports.
                This prevents matching mailbox data to specific users.
              </p>
              <p className="mb-3">
                A tenant admin can disable this setting to view mailbox usage data:
              </p>
              <a
                href={getReportsSettingsUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-2 border border-amber-300 shadow-sm text-sm font-medium rounded-md text-amber-800 bg-amber-100 hover:bg-amber-200 mb-3"
              >
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Open Reports Settings in M365 Admin Center
              </a>
              <p className="text-xs text-amber-700 mb-3">
                Uncheck "Display concealed user, group, and site names in all reports" and save.
              </p>
              <p>
                Alternatively, view mailbox details directly in the{' '}
                <a
                  href={getExchangeAdminCenterUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline hover:text-amber-900"
                >
                  Exchange Admin Center
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!usage) {
    return (
      <div className="bg-white shadow sm:rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Mail</h3>
          <a
            href={getExchangeAdminCenterUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            Open in Exchange Admin
          </a>
        </div>
        <div className="text-center py-8 text-gray-500">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <p className="mt-2">No mailbox data available for this user.</p>
          <p className="mt-1 text-sm">The user may not have a mailbox, or data hasn't been generated yet.</p>
        </div>
      </div>
    )
  }

  const storageStatus = getStorageStatus()
  const storagePercentage = getStoragePercentage()

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Mail</h3>
          <div className="flex space-x-2">
            <a
              href={getAdminCenterUserUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Admin Center
            </a>
            <a
              href={getExchangeAdminCenterUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-1.5 border border-primary-300 shadow-sm text-sm font-medium rounded-md text-primary-700 bg-primary-50 hover:bg-primary-100"
            >
              <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Exchange Admin
            </a>
          </div>
        </div>

        <div className="space-y-4">
          {/* Storage Usage */}
          <div className="py-3 border-b border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Storage Used</p>
                  <p className="text-sm text-gray-500">
                    {formatBytes(usage.storageUsedInBytes)} of {formatBytes(usage.prohibitSendReceiveQuotaInBytes)}
                  </p>
                </div>
              </div>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                storageStatus.color === 'red' ? 'bg-red-100 text-red-800' :
                storageStatus.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                'bg-green-100 text-green-800'
              }`}>
                {storageStatus.label}
              </span>
            </div>
            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${
                  storageStatus.color === 'red' ? 'bg-red-500' :
                  storageStatus.color === 'yellow' ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${storagePercentage}%` }}
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">{storagePercentage.toFixed(1)}% used</p>
          </div>

          {/* Item Count */}
          <div className="flex items-center justify-between py-3 border-b border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">Total Items</p>
                <p className="text-sm text-gray-500">Emails, calendar items, contacts, etc.</p>
              </div>
            </div>
            <span className="text-sm font-medium text-gray-900">{usage.itemCount.toLocaleString()}</span>
          </div>

          {/* Archive Status */}
          <div className="flex items-center justify-between py-3 border-b border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">Archive Mailbox</p>
                <p className="text-sm text-gray-500">In-place archive for older items</p>
              </div>
            </div>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              usage.hasArchive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}>
              {usage.hasArchive ? 'Enabled' : 'Disabled'}
            </span>
          </div>

          {/* Last Activity */}
          <div className="flex items-center justify-between py-3 border-b border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">Last Activity</p>
                <p className="text-sm text-gray-500">Last time the mailbox was accessed</p>
              </div>
            </div>
            <span className="text-sm text-gray-900">{formatDate(usage.lastActivityDate)}</span>
          </div>

          {/* Quotas */}
          <div className="py-3">
            <div className="flex items-center mb-2">
              <svg className="h-5 w-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <p className="text-sm font-medium text-gray-900">Quota Limits</p>
            </div>
            <div className="ml-8 grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Warning at</p>
                <p className="font-medium">{formatBytes(usage.issueWarningQuotaInBytes)}</p>
              </div>
              <div>
                <p className="text-gray-500">Send blocked at</p>
                <p className="font-medium">{formatBytes(usage.prohibitSendQuotaInBytes)}</p>
              </div>
              <div>
                <p className="text-gray-500">Full at</p>
                <p className="font-medium">{formatBytes(usage.prohibitSendReceiveQuotaInBytes)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Quick Actions</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Out of Office */}
            <a
              href={getOutOfOfficeUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors"
            >
              <div className="flex-shrink-0 h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg className="h-5 w-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-900">Out of Office</p>
                <p className="text-xs text-gray-500">Set automatic replies</p>
              </div>
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>

            {/* Grant Mailbox Access */}
            <a
              href={getMailboxDelegationUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors"
            >
              <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-900">Mailbox Access</p>
                <p className="text-xs text-gray-500">Manage delegation</p>
              </div>
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>

            {/* Convert to Shared Mailbox */}
            <a
              href={getConvertMailboxUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors"
            >
              <div className="flex-shrink-0 h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="h-5 w-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-900">Convert Mailbox</p>
                <p className="text-xs text-gray-500">User to Shared</p>
              </div>
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            These actions open in Exchange Admin Center
          </p>
        </div>

        {/* Info box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm text-blue-700">
              <p>
                Usage data is refreshed daily by Microsoft. Last report: {formatDate(usage.reportRefreshDate)}.
                For real-time data and detailed management options, use the{' '}
                <a
                  href={getExchangeAdminCenterUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline hover:text-blue-800"
                >
                  Exchange Admin Center
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
